import {
  BlockStatus,
  isLive,
  MarkOutcome,
  RecordType,
  type IdSpan,
  type LiveTimeBlock,
  type Span,
  type TimeBlockRecord,
} from '@wiztime/core';
import {
  queries,
  type EditBlocksPatch,
  type OverlapResolution,
  type Store,
} from '@wiztime/storage';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRunCommand } from '../../hooks/useRunCommand';
import { useStore } from '../../hooks/useServices';
import { useUndoVersionReader } from '../../hooks/useUndoVersion';
import { readStorage, writeStorage } from '../../lib/safeStorage';
import { FLAG_ON, StorageKey } from '../../lib/storageKeys';
import { reportConfirm } from '../../shell/confirmToast';
import { toastInfo, toastUndo } from '../../shell/toastStore';
import { useDayUi } from './dayUiStore';
import type { DisplayGroup } from './timeline/laneModel';

/** The first-overlap notice stays a little longer than an ordinary info toast (ui-redesign §2.1.5). */
export const OVERLAP_NOTICE_MS = 8_000;

/**
 * One-time notice the first time a local create, move or resize produces an overlap of two
 * confirmed blocks (ui-redesign §2.1.5). Overlaps arriving by sync or import never pass
 * through these commands, so they never trigger it. The day is re-read from the store: the
 * caller's view data may not have caught up with the write yet.
 */
async function noticeFirstOverlap(
  store: Store,
  written: readonly (TimeBlockRecord | null)[],
  message: () => string,
): Promise<void> {
  if (readStorage(StorageKey.OverlapNoticeDone) === FLAG_ON) return;
  const confirmed = written.filter(
    (record): record is LiveTimeBlock =>
      record !== null && isLive(record) && record.data.status === BlockStatus.Confirmed,
  );
  for (const record of confirmed) {
    const day = await queries.blocksForDate(store.repo, record.data.personalDate);
    const overlapping = day.some(
      (other) =>
        other.id !== record.id &&
        isLive(other) &&
        other.data.status === BlockStatus.Confirmed &&
        other.data.startMin < record.data.endMin &&
        other.data.endMin > record.data.startMin,
    );
    if (!overlapping) continue;
    writeStorage(StorageKey.OverlapNoticeDone, FLAG_ON);
    toastInfo(message(), OVERLAP_NOTICE_MS);
    return;
  }
}

/** Optional fields of a created block; `select` makes the new block the selected one. */
export interface CreateExtras {
  readonly note?: string | null;
  readonly title?: string | null;
  readonly select?: boolean;
}

export interface DayCommands {
  /**
   * Create a confirmed block. Returns the `MarkOutcome` (2026-10b §2.5): `Created`,
   * `Duplicate` when the exact-duplicate guard suppressed it, or `Failed`. Quick-mark
   * callers await it — the "once" is consumed only on `Created` (§4.3).
   */
  createBlock(
    date: string,
    span: Span,
    projectId: string,
    extras?: CreateExtras,
  ): Promise<MarkOutcome>;
  updateBlockSpan(id: string, span: Span): Promise<void>;
  /** Apply a merged-group resize: new member spans plus deleted members, one undo step (§2.3). */
  resizeGroup(changes: readonly IdSpan[], deleteIds: readonly string[]): Promise<void>;
  /**
   * Week-view cross-day drag (ui-redesign §2.8): move a block or merged group to another
   * personal date in one transaction and one undo step; start, end and `tz` keep their values.
   */
  moveBlocksToDate(ids: readonly string[], targetDate: string): Promise<void>;
  /**
   * The picker's single commit (2026-10b §2.5): write the changed fields of a block or of
   * every member of a merged group in one undo step. `savedMessage` adds the "saved" undo
   * toast (Esc / outside-press closes, §5.4). Returns whether the write succeeded.
   */
  editBlocks(
    ids: readonly string[],
    patch: EditBlocksPatch,
    savedMessage?: string,
  ): Promise<boolean>;
  confirmGroup(group: DisplayGroup): Promise<void>;
  clearGroup(group: DisplayGroup): Promise<void>;
  resolveConflict(idA: string, idB: string, mode: OverlapResolution): Promise<void>;
  /** 「重叠部分改计入本块」(ui-redesign §2.1.5): one undo step that truly restores attribution. */
  recountOverlap(id: string): Promise<void>;
}

/**
 * The day view's write operations. Each is one store command, hence one undo step (clearing a merged
 * group included). Failures become toasts; clearing offers an undo toast. After a block is created,
 * re-projected or confirmed, keyboard focus moves onto it so that entry can continue from there.
 */
export function useDayCommands(): DayCommands {
  const store = useStore();
  const run = useRunCommand();
  const readVersion = useUndoVersionReader();
  const { t } = useTranslation();

  return useMemo<DayCommands>(
    () => ({
      async createBlock(date, span, projectId, extras) {
        const { select, ...fields } = extras ?? {};
        const result = await run(() => store.createBlock(date, span, projectId, fields));
        if (!result.ok) return MarkOutcome.Failed;
        if (result.value !== null) {
          if (select === true) useDayUi.getState().selectAuto(result.value.id);
          useDayUi.getState().requestFocus(result.value.id);
          void noticeFirstOverlap(store, [result.value], () => t('toast.firstOverlap'));
          return MarkOutcome.Created;
        }
        // A null value means the exact-duplicate guard suppressed the creation (ui-redesign
        // §2.1.4): tell the user the span is already recorded, naming the project.
        const project = await queries.getRecord(store.repo, projectId);
        const name = project?.type === RecordType.Project ? (project.data?.name ?? '') : '';
        toastInfo(t('toast.duplicateBlock', { project: name }));
        return MarkOutcome.Duplicate;
      },
      async updateBlockSpan(id, span) {
        const result = await run(() =>
          store.updateBlock(id, { startMin: span.startMin, endMin: span.endMin }),
        );
        if (result.ok) void noticeFirstOverlap(store, [result.value], () => t('toast.firstOverlap'));
      },
      async resizeGroup(changes, deleteIds) {
        const result = await run(() => store.updateBlockSpans(changes, deleteIds));
        if (!result.ok) return;
        void noticeFirstOverlap(store, result.value, () => t('toast.firstOverlap'));
        if (deleteIds.length === 0) return;
        // Deleting members by dragging a group edge inward is silent on screen: the undo toast
        // is the only confirmation (ui-redesign §2.3, no pre-release dialog).
        toastUndo(
          t('toast.groupResizeDeleted', { count: deleteIds.length }),
          t('toast.undo'),
          readVersion(),
          () => {
            void run(() => store.undo());
          },
        );
      },
      async moveBlocksToDate(ids, targetDate) {
        await run(() => store.moveBlocksToDate(ids, targetDate));
      },
      async editBlocks(ids, patch, savedMessage) {
        const first = ids[0];
        if (first === undefined) return false;
        const result = await run(() => store.editBlocks(ids, patch));
        if (!result.ok) return false;
        if (patch.projectId !== undefined) useDayUi.getState().requestFocus(first);
        if (patch.span !== undefined) {
          void noticeFirstOverlap(store, result.value, () => t('toast.firstOverlap'));
        }
        if (savedMessage !== undefined && result.value.length > 0) {
          toastUndo(savedMessage, t('toast.undo'), readVersion(), () => {
            void run(() => store.undo());
          });
        }
        return true;
      },
      async confirmGroup(group) {
        const result =
          group.members.length > 1
            ? await run(() => store.setGroupProject(group.memberIds, group.projectId))
            : await run(() => store.confirmDraft(group.key));
        if (!result.ok) return;
        if (result.value.removedAsDuplicate.length === 0) {
          useDayUi.getState().requestFocus(group.key);
          return;
        }
        // Confirming silently stays silent; a duplicate guard hit is reported with undo (§10.1).
        await reportConfirm(store, t, result.value, group.projectId, readVersion(), () => {
          void run(() => store.undo());
        });
      },
      async clearGroup(group) {
        if (group.memberIds.length === 0) return;
        const result = await run(() => store.clearBlocks(group.memberIds));
        if (!result.ok) return;
        // "Deleted N blocks", not "cleared" (2026-10b §6.1): with titles and notes on blocks,
        // "clear" would read as "empty the content, keep the block".
        toastUndo(t('toast.deletedBlocks', { count: group.memberIds.length }), t('toast.undo'), readVersion(), () => {
          void run(() => store.undo());
        });
      },
      async resolveConflict(idA, idB, mode) {
        await run(() => store.resolveOverlap(idA, idB, mode));
      },
      async recountOverlap(id) {
        const result = await run(() => store.setCountRank(id));
        if (!result.ok) return;
        // The hatch moves to the other block; the toast confirms it and offers the one-step undo.
        toastUndo(t('toast.recounted'), t('toast.undo'), readVersion(), () => {
          void run(() => store.undo());
        });
      },
    }),
    [store, run, readVersion, t],
  );
}
